/**
 * Org health score and its sparkline.
 *
 * The score used to be `null` in demo mode, which rendered the dashboard's
 * most prominent card as an empty state - the first thing a visitor saw was
 * the product failing to say anything. It is derived here from the same
 * profile as every other number, so a repo the metrics call slow also scores
 * badly rather than carrying an unrelated grade.
 */

import type {
  HealthScoreResponse,
  HealthScoreTrendResponse,
  HealthScoreTrendPoint,
} from '@/types/retention';
import { resolveProfile, rollUpProfile } from '../fixture';
import { demoDate, jitter, rngFor, type DemoSeedInput } from '../seed';

export interface DemoHealthParams {
  days: number;
  repoId?: string | null;
  teamId?: string | null;
  excludeBots: boolean;
}

const seedInput = (params: DemoHealthParams, route: string): DemoSeedInput => ({
  route,
  days: params.days,
  repoId: params.repoId,
  teamId: params.teamId,
  excludeBots: params.excludeBots,
});

const clamp = (n: number) => Math.max(0, Math.min(100, Math.round(n)));

function letterGrade(score: number): string {
  if (score >= 90) return 'A';
  if (score >= 80) return 'B';
  if (score >= 70) return 'C';
  if (score >= 60) return 'D';
  return 'F';
}

/**
 * Category scores, each anchored to the profile field it actually describes.
 *
 * Anchoring matters: a visitor who sees "Quality 94" beside a 13% test failure
 * rate stops believing the page. The org baseline is the roll-up, so each
 * category is scored relative to it.
 */
function categories(params: DemoHealthParams): Record<string, number> {
  const profile = resolveProfile(params.repoId, params.teamId);
  const org = rollUpProfile();
  const rng = rngFor(seedInput(params, 'health/categories'), 'scores');

  return {
    velocity: clamp((org.cycleTimeHours / profile.cycleTimeHours) * 78 * jitter(rng, 0.05)),
    quality: clamp((100 - profile.testFailureRateBase * 2.6) * jitter(rng, 0.04)),
    safety: clamp(profile.reviewCoverageBase * jitter(rng, 0.03)),
    stability: clamp((100 - (profile.churnMultiplier - 0.8) * 42) * jitter(rng, 0.05)),
    responsiveness: clamp((1 - profile.waitForReviewShare) * 128 * jitter(rng, 0.05)),
  };
}

export function buildDemoHealthScore(params: DemoHealthParams): HealthScoreResponse {
  const scores = categories(params);
  const values = Object.values(scores);
  const overall = clamp(values.reduce((a, b) => a + b, 0) / values.length);

  const rng = rngFor(seedInput(params, 'health/overall'), 'previous');
  const previous = clamp(overall * jitter(rng, 0.06));
  const delta = overall - previous;

  return {
    overall_score: overall,
    categories: scores,
    previous_score: previous,
    trend: Math.abs(delta) <= 1 ? 'stable' : delta > 0 ? 'up' : 'down',
    letter_grade: letterGrade(overall),
    period_start: demoDate(params.days),
    period_end: demoDate(0),
    scope: params.repoId ? 'repository' : params.teamId ? 'team' : 'organization',
    sparkline_available: true,
  };
}

export function buildDemoHealthScoreTrend(params: DemoHealthParams): HealthScoreTrendResponse {
  const scores = categories(params);
  const overall = clamp(Object.values(scores).reduce((a, b) => a + b, 0) / Object.values(scores).length);

  // Weekly points: the trend endpoint is always called with a 90-day window
  // and the sparkline reads better at ~13 points than at 90.
  const points = 13;
  const series = (base: number, subKey: string): HealthScoreTrendPoint[] => {
    const rng = rngFor(seedInput(params, 'health/trend'), subKey);
    return Array.from({ length: points }, (_, i) => {
      const weeksAgo = points - 1 - i;
      // A gentle climb towards today, so the demo shows a team improving -
      // which is the story the product is meant to help create.
      const drift = 1 - weeksAgo * 0.006;
      return {
        date: demoDate(weeksAgo * 7),
        value: clamp(base * drift * jitter(rng, 0.05)),
      };
    });
  };

  return {
    overall: series(overall, 'overall'),
    velocity: series(scores.velocity, 'velocity'),
    quality: series(scores.quality, 'quality'),
    safety: series(scores.safety, 'safety'),
    stability: series(scores.stability, 'stability'),
    responsiveness: series(scores.responsiveness, 'responsiveness'),
  };
}
